import {
  type ReactElement,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Location } from "react-router-dom";

interface RefererContextValue {
  /** The first non-login location the app saw, frozen until cleared. */
  path?: Location;
  /**
   * Capture the referer once — the first non-login location. Later calls are
   * no-ops until clearReferer() runs (mirrors the old reducer's `initialized`
   * guard, so a deep link is remembered for the post-login redirect).
   */
  setReferer: (location: Location) => void;
  /** Reset the captured referer (on logout). */
  clearReferer: () => void;
}

const RefererContext = createContext<RefererContextValue | undefined>(
  undefined,
);

export function RefererProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [path, setPath] = useState<Location | undefined>(undefined);
  const initialized = useRef(false);

  const setReferer = useCallback((location: Location) => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;
    setPath(location.pathname === "/auth/login" ? undefined : location);
  }, []);

  const clearReferer = useCallback(() => {
    initialized.current = true;
    setPath(undefined);
  }, []);

  const value = useMemo(
    () => ({ path, setReferer, clearReferer }),
    [path, setReferer, clearReferer],
  );

  return (
    <RefererContext.Provider value={value}>{children}</RefererContext.Provider>
  );
}

function useRefererContext(): RefererContextValue {
  const ctx = useContext(RefererContext);
  if (!ctx) {
    throw new Error("useReferer must be used within a RefererProvider");
  }
  return ctx;
}

export function useReferer(): Location | undefined {
  return useRefererContext().path;
}

export function useSetReferer(): (location: Location) => void {
  return useRefererContext().setReferer;
}

export function useClearReferer(): () => void {
  return useRefererContext().clearReferer;
}
